import { createApi } from '@reduxjs/toolkit/query/react';

import { IComment, IConfigComment } from '@/interfaces/comments';
import { IPaginationParams, IPaginationResponse } from '@/interfaces/pagination';

import { axiosBaseQuery, paginationConfig } from './query';

type AdditionalFilters = { allComments: boolean; ownComments: boolean };

export const commentsApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'commentsApi',
  tagTypes: ['list'],
  endpoints(build) {
    return {
      deleteComment: build.mutation<IComment, { lessonId: string | undefined; isReply: boolean; commentId: number }>({
        invalidatesTags: ['list'],
        query: ({ lessonId, commentId, isReply }) => ({
          formatResponse: response => response.data.data,
          url: `/learner/lessons/${lessonId}/${isReply ? 'reply' : 'comments'}/${commentId}`,
          method: 'DELETE'
        })
      }),
      saveReply: build.mutation<IComment, { lessonId: string | undefined; parentId: number; comment: string }>({
        invalidatesTags: ['list'],
        query: ({ lessonId, comment, parentId }) => ({
          formatResponse: response => response.data.data,
          url: `/learner/lessons/${lessonId}/comments/${parentId}/reply`,
          method: 'POST',
          data: {
            text: comment
          }
        })
      }),
      saveComment: build.mutation<IComment, { lessonId: string | undefined; comment: string }>({
        invalidatesTags: ['list'],
        query: ({ lessonId, comment }) => ({
          formatResponse: response => response.data.data,
          url: `/learner/lessons/${lessonId}/comments`,
          method: 'POST',
          data: {
            text: comment
          }
        })
      }),
      getReplies: build.query<
        IPaginationResponse<IComment[]>,
        { lessonId: string | undefined; parentId: number; params?: Partial<IPaginationParams> }
      >({
        ...paginationConfig,
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          return endpointName + queryArgs.parentId;
        },
        providesTags: ['list'],
        query: ({ lessonId, parentId, params }) => ({
          url: `/learner/lessons/${lessonId}/comments/${parentId}`,
          params: {
            ...params,
            reply: true
          }
        })
      }),
      getComments: build.query<
        IPaginationResponse<IComment[], AdditionalFilters>,
        { lessonId: string | undefined; params: Partial<IPaginationParams & AdditionalFilters> }
      >({
        ...paginationConfig,
        providesTags: ['list'],
        query: ({ lessonId, params }) => ({
          url: `/learner/lessons/${lessonId}/comments`,
          params
        })
      }),
      getConfigComments: build.query<IConfigComment, { lessonId: string; producerId: number }>({
        query: ({ producerId, lessonId }) => ({
          formatResponse: response => response.data,
          url: `/learner/lessons/${lessonId}/comments/config/${producerId}`
        })
      })
    };
  }
});

export const {
  useGetCommentsQuery,
  useSaveCommentMutation,
  useSaveReplyMutation,
  useDeleteCommentMutation,
  useGetConfigCommentsQuery
} = commentsApi;
